<template>
  <div>
    <MessagesNav
      :unreadMessageCount="unreadMessageCount"
      :unreadInformationCount="unreadInformationCount"
    ></MessagesNav>

    <div v-if="initialized && !canApplyOrHasApplications">
      <CantUseFeature />
    </div>
    <div v-else class="container">
      <PullToRefresh
        @pull="pullToFetchMessages"
        :isShowingPullToRefreshLoading="isShowingPullToRefreshLoading"
      >
        <ul v-if="messageRooms.length > 0" data-testid="message-rooms">
          <li
            v-for="messageRoom of messageRooms"
            :key="'room-' + messageRoom.officeId"
          >
            <RouterLink
              :to="{
                name: 'MessageRoomDetail',
                params: { officeId: messageRoom.officeId }
              }"
            >
              <div
                class="message-room bg-white border-bottom border-outline-color p-3"
              >
                <div
                  class="d-flex justify-content-end align-items-center w-100"
                >
                  <div
                    class="flex-grow-1 d-flex flex-column justify-content-between"
                  >
                    <div class="office-info">
                      <p class="office-name-label text-size-14 text-weight-500">
                        {{ messageRoom.officeName }}
                      </p>
                      <MuteOnSvg
                        v-if="messageRoom.isMutedOffice"
                        class="mute-on-icon"
                      />
                    </div>
                    <p
                      v-if="messageRoom.startNextWorkAt"
                      class="text-mild text-size-12 mt-1"
                      data-testid="next-work-label"
                    >
                      {{ getNextWorkLabel(messageRoom) }}
                    </p>
                  </div>

                  <div
                    class="notification flex-shrink-0 d-flex flex-column justify-content-between align-items-end"
                  >
                    <p
                      class="last-message-at-label text-mild text-size-12 ml-3"
                    >
                      {{ toDisplayTime(messageRoom.lastMessageAt) }}
                    </p>
                    <AppBadge
                      variant="red"
                      data-testid="app-badge"
                      v-if="messageRoom.unreadCount > 0"
                      class="flex-shrink-0"
                    >
                      {{ messageRoom.unreadCount }}
                    </AppBadge>
                    <BBadge
                      v-if="messageRoom.requireReply"
                      pill
                      variant="primary"
                      class="flex-shrink-0 text-weight-400 ml-3 mt-1"
                      data-testid="require-reply-badge"
                      >要挨拶</BBadge
                    >
                  </div>

                  <div class="arrow flex-shrink-0">
                    <FeatherIcon
                      icon="chevron-right"
                      size="1x"
                      class="text-gray-05 ml-2"
                    />
                  </div>
                </div>
              </div>
            </RouterLink>
          </li>
        </ul>
        <p
          v-else
          class="p-3 m-3 bg-white text-size-14 text-center"
          data-testid="message-rooms-empty"
        >
          まだメッセージはありません
        </p>
      </PullToRefresh>
    </div>
    <AppFooter />
    <BlockBack />
  </div>
</template>

<script lang="ts">
import AppFooter from "@/components/organisms/common/AppFooter.vue";
import AppBadge from "@/components/atoms/other/AppBadge.vue";
import FeatherIcon from "@/components/atoms/other/FeatherIcon.vue";
import PullToRefresh from "@/components/molecules/other/PullToRefresh.vue";
import { accountService } from "@/app/services/AccountService";
import { messageService } from "@/app/services/MessageService";
import { salaryService } from "@/app/services/SalaryService";
import { authStore } from "@/store/auth";
import { relativeTime } from "@/utils/relativeTime";
import { ErrorType, showErrorModal } from "@/utils/showErrorModal";
// @ts-ignore
import "mobile-pull-to-refresh/dist/styles/ios/style.css";
import BlockBack from "@/components/organisms/common/BlockBack.vue";
import { Component, Vue } from "vue-facing-decorator";
import { MessageRoom } from "@/types/order/MessageRoom";
import CantUseFeature from "@/components/organisms/accounts/CantUseFeature.vue";
import MessagesNav from "@/components/organisms/messages/MessagesNav.vue";
import verifiedStatus from "@/models/verifiedStatus";
import { applicationStore } from "@/store/application";
import { messageStore } from "@/store/message";
import { formatFromISOString, parseISO } from "@/utils/date";
import MuteOnSvg from "@/assets/svg/MuteOn.svg?component";

@Component({
  components: {
    AppFooter,
    AppBadge,
    FeatherIcon,
    BlockBack,
    PullToRefresh,
    MessagesNav,
    MuteOnSvg,
    CantUseFeature
  }
})
export default class MessageRooms extends Vue {
  messageRooms: MessageRoom[] = [];
  isShowingPullToRefreshLoading = true;
  hasVerifiedQualification = false;
  hasBankAccount = false;
  initialized = false;

  get canApplyOrHasApplications() {
    if (
      (authStore.user && authStore.user.workCount > 0) ||
      applicationStore.applications.length > 0
    ) {
      return true;
    }
    return (
      authStore.hasVerifiedAccountInfo &&
      authStore.hasRegisteredBusinessInfo &&
      this.hasVerifiedQualification &&
      this.hasBankAccount
    );
  }

  get unreadMessageCount() {
    return messageStore.unreadMessageCount;
  }

  get unreadInformationCount() {
    return messageStore.unreadInformationCount;
  }

  async created() {
    try {
      this.$loading.show();
      await Promise.all([
        this.fetchMessageRooms(),
        this.fetchHasBankAccount(),
        this.fetchHasVerifiedQualification()
      ]);
      this.initialized = true;
    } catch (error) {
      await showErrorModal(this, error as ErrorType);
    } finally {
      this.$loading.hide();
    }
  }

  async fetchHasBankAccount() {
    this.hasBankAccount = await salaryService.fetchHasBankAccount();
  }

  async fetchHasVerifiedQualification() {
    const { results } = await accountService.fetchQualifications();
    this.hasVerifiedQualification = results.some(
      q => q.status === verifiedStatus.VERIFIED
    );
  }

  async pullToFetchMessages() {
    try {
      this.isShowingPullToRefreshLoading = true;
      await this.fetchMessageRooms();
    } catch (error) {
      showErrorModal(this, error as ErrorType);
    } finally {
      this.isShowingPullToRefreshLoading = false;
    }
  }

  async fetchMessageRooms() {
    if (!authStore.user) return;

    // メッセージルームを取得
    this.messageRooms = await messageService.fetchMessageRooms();
  }

  // 次回勤務日を出力
  getNextWorkLabel(messageRoom: MessageRoom) {
    if (!messageRoom.startNextWorkAt) return "";

    const startWorkDateStr = formatFromISOString(
      messageRoom.startNextWorkAt,
      "MM月dd日"
    );
    const finishNextWorkDateStr = formatFromISOString(
      messageRoom.finishNextWorkAt,
      "MM月dd日"
    );
    const startWorkTimeStr = formatFromISOString(
      messageRoom.startNextWorkAt,
      "HH:mm"
    );
    const finishWorkTimeStr = formatFromISOString(
      messageRoom.finishNextWorkAt,
      "HH:mm"
    );
    if (startWorkDateStr === finishNextWorkDateStr) {
      return `次回勤務：${startWorkDateStr} ${startWorkTimeStr}-${finishWorkTimeStr}`;
    } else {
      return `次回勤務：${startWorkDateStr} ${startWorkTimeStr}-${finishNextWorkDateStr} ${finishWorkTimeStr}`;
    }
  }

  toDisplayTime(isoDate: string) {
    const date = parseISO(isoDate);
    return relativeTime(date);
  }
}
</script>
<style lang="scss" scoped>
.container {
  position: relative;
  padding: 0;

  .message-room {
    display: flex;
    align-items: center;
    min-height: 72px;

    .notification {
      width: 68px;
    }

    .arrow {
      display: flex;
      align-items: center;
      width: 30px;
      height: 20px;
    }

    .office-info {
      display: flex;
      gap: 8px;
      align-items: center;

      .office-name-label {
        line-height: 20px;
      }

      .mute-on-icon {
        min-width: 20px;
      }
    }

    .last-message-at-label {
      line-height: 20px;
    }
  }
}
</style>
